import { useSearchkit } from '@searchkit/client';
import { useContext, useEffect, useState } from 'react';
import { useSavePreferences } from '../../auth/hooks/savePreferences';
import { UserContext } from '../../auth/UserContext';
import { FILTER_CONFIG } from '../../constants/constants';

type UseFilterFacetOptions = {
    preferenceEntityKey?: string;
    defaultFilterAutoApply: boolean;
};

type UseFilterFacetReturn = {
    autoApply: boolean;
    hasPendingFilters: boolean;
    canApply: boolean;
    handleFiltersChanged: () => void;
    handleToggleAutoApply: (value: boolean) => Promise<void>;
    handleApply: () => void;
    clearPendingFilters: () => void;
    handleClearAll: () => void;
};

export const useFilterFacet = ({
    preferenceEntityKey,
    defaultFilterAutoApply
}: UseFilterFacetOptions): UseFilterFacetReturn => {
    const api = useSearchkit();
    const { preferencesLoaded, preferences, userProfile, application, refreshPreferences } = useContext(UserContext);
    const { savePreferences } = useSavePreferences();
    const [autoApply, setAutoApply] = useState(defaultFilterAutoApply);
    const [preferenceId, setPreferenceId] = useState<string>();
    const [hasPendingFilters, setHasPendingFilters] = useState(false);

    useEffect(() => {
        if (preferenceEntityKey && preferencesLoaded && preferences) {
            const preferenceObj = preferences.find(
                (p) =>
                    p.view === preferenceEntityKey &&
                    p.key === FILTER_CONFIG &&
                    p.application === application.name &&
                    p.agent === ''
            );
            if (preferenceObj) {
                try {
                    const value = JSON.parse(preferenceObj.value);
                    if (value?.autoApply !== undefined) {
                        setAutoApply(value.autoApply);
                    }
                    setPreferenceId(preferenceObj.id);
                } catch (error) {
                    console.error('Failed to parse filter auto-apply preference:', error);
                }
            }
        }
    }, [preferencesLoaded, preferences, preferenceEntityKey, application.name]);

    useEffect(() => {
        if (autoApply) {
            setHasPendingFilters(false);
        }
    }, [autoApply]);

    const canApply = !autoApply && hasPendingFilters;

    const handleFiltersChanged = () => {
        if (!autoApply) {
            setHasPendingFilters(true);
        }
    };

    const constructPreferenceInput = (autoApplyValue: boolean) => ({
        ...(preferenceId && { identity: { id: preferenceId } }),
        accountId: userProfile.accountId,
        view: preferenceEntityKey,
        key: FILTER_CONFIG,
        value: JSON.stringify({ autoApply: autoApplyValue }),
        agent: ''
    });

    const constructUpdatedPreferences = (resultId: string, autoApplyValue: boolean) => {
        const preferenceValue = JSON.stringify({ autoApply: autoApplyValue });
        const updatedPreferences = preferences.map((p) => (p.id === resultId ? { ...p, value: preferenceValue } : p));

        if (!updatedPreferences.find((p) => p.id === resultId)) {
            updatedPreferences.push({
                id: resultId,
                accountId: userProfile.accountId,
                application: application.name,
                view: preferenceEntityKey,
                key: FILTER_CONFIG,
                value: preferenceValue,
                agent: '',
                name: '',
                summary: { about: '', headline: '' },
                headline: '',
                uri: '',
                shared: false
            });
        }

        return updatedPreferences;
    };

    const saveAutoApplyPreference = async (value: boolean) => {
        const result = await savePreferences({
            preferenceInput: constructPreferenceInput(value),
            shareInput: null,
            showSuccessMessages: false
        });

        if (result?.id) {
            setPreferenceId(result.id);
            const updatedPreferences = constructUpdatedPreferences(result.id, value);
            await refreshPreferences(undefined, updatedPreferences, false);
        }
    };

    const updateAutoApplyState = (value: boolean) => {
        setAutoApply(value);
        setHasPendingFilters(false);

        if (value && hasPendingFilters) {
            api.search();
        }
    };

    const handleToggleAutoApply = async (value: boolean) => {
        updateAutoApplyState(value);

        if (!preferenceEntityKey) return;

        try {
            await saveAutoApplyPreference(value);
        } catch (error) {
            console.error('Failed to save filter auto-apply preference:', error);
            setAutoApply(!value);
        }
    };

    const clearPendingFilters = () => {
        setHasPendingFilters(false);
    };

    const handleApply = () => {
        if (!canApply) return;
        clearPendingFilters();
        api.search();
    };

    const handleClearAll = () => {
        clearPendingFilters();
        api.search();
    };

    return {
        autoApply,
        hasPendingFilters,
        canApply,
        handleFiltersChanged,
        handleToggleAutoApply,
        handleApply,
        clearPendingFilters,
        handleClearAll
    };
};
